import { useState, useEffect, useCallback } from 'react';
import api from '../utils/api';

export const useContacts = () => {
  const [contacts, setContacts] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchContacts = useCallback(async (params = {}) => {
    try {
      setLoading(true);
      setError(null);
      
      const queryParams = new URLSearchParams();
      if (params.search) queryParams.append('search', params.search);
      if (params.filter) queryParams.append('filter', params.filter);
      if (params.limit) queryParams.append('limit', params.limit);
      if (params.offset) queryParams.append('offset', params.offset);
      
      const queryString = queryParams.toString();
      const url = queryString ? `/contacts?${queryString}` : '/contacts';
      
      const response = await api.get(url);
      setContacts(response.data.contacts || []);
      return response.data;
    } catch (err) {
      console.error('Error fetching contacts:', err);
      setError(err.response?.data?.error || 'Failed to fetch contacts');
      return { contacts: [], total: 0 };
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchStats = useCallback(async () => {
    try {
      const response = await api.get('/contacts/stats');
      setStats(response.data);
      return response.data;
    } catch (err) {
      console.error('Error fetching stats:', err);
      return null;
    }
  }, []);

  const getContact = useCallback(async (id) => {
    try {
      const response = await api.get(`/contacts/${id}`);
      return response.data.contact;
    } catch (err) {
      console.error('Error fetching contact:', err);
      throw err;
    }
  }, []);

  const createContact = useCallback(async (contactData) => {
    try {
      const response = await api.post('/contacts', contactData);
      await fetchContacts();
      await fetchStats();
      return response.data.contact;
    } catch (err) {
      console.error('Error creating contact:', err);
      throw err;
    }
  }, [fetchContacts, fetchStats]);

  const updateContact = useCallback(async (id, contactData) => {
    try {
      const response = await api.put(`/contacts/${id}`, contactData);
      await fetchContacts();
      await fetchStats();
      return response.data.contact;
    } catch (err) {
      console.error('Error updating contact:', err);
      throw err;
    }
  }, [fetchContacts, fetchStats]);

  const deleteContact = useCallback(async (id) => {
    try {
      await api.delete(`/contacts/${id}`);
      await fetchContacts();
      await fetchStats();
    } catch (err) {
      console.error('Error deleting contact:', err);
      throw err;
    }
  }, [fetchContacts, fetchStats]);

  const toggleFavorite = useCallback(async (contact) => {
    try {
      await updateContact(contact.id, {
        ...contact,
        is_favorite: !contact.is_favorite
      });
    } catch (err) {
      console.error('Error toggling favorite:', err);
      throw err;
    }
  }, [updateContact]);

  useEffect(() => {
    fetchContacts();
    fetchStats();
  }, [fetchContacts, fetchStats]);

  return {
    contacts,
    stats,
    loading,
    error,
    fetchContacts,
    fetchStats,
    getContact,
    createContact,
    updateContact,
    deleteContact,
    toggleFavorite
  };
};

